<template>
  <section :class="[{ carded: !isTransparent }, routeClass]">
    <!-- Teleport landing spot for a carded screen's primary CTA on phones.
         On phone the onboarding "weiter" button is lifted out of the card and
         docked here — bottom-right, beside the corner frog — so the frog gets
         the whole bottom-left corner (see LaunchFirstStart's <Teleport>).
         Empty + display:none on tablet, where the button stays in the card.
         Rendered before the card so the target exists in the DOM by the time
         RouterView's child mounts and resolves its Teleport. -->
    <div id="launch-action"></div>

    <div class="container" :class="{ transparent: isTransparent, unboxed: isWeb }">
      <picture class="logo">
        <img :src="logoIcon" alt="LingoTalk" />
      </picture>

      <RouterView />
    </div>

    <!-- Viewport-fixed launch chrome. Lives at the layout level (not in the
         child screen) so any future launch route that needs the cog/pair
         buttons opts in by adding its name to the allow-list above — no
         per-screen markup duplication. Mirrors the existing pattern where
         the layout owns :deep(div.trial), :deep(div.actions), :deep(div.status). -->
    <!-- Therapist chrome: the way back out of LingoTalk into the portal, in
         the corner the design puts it (docs/screens/launch-initial.png). By
         platform, not by route — the web build only ever renders therapist
         routes under this layout (router role guard), and the patient build
         has no portal to go back to. Never collides with the pair/linked
         button below: that one is allow-listed to a patient route. -->
    <PortalButton v-if="isWeb" />

    <template v-if="showTopChrome">
      <div class="cog">
        <Button variant="settings" @click="openSettings" />
      </div>
      <!-- One corner, two states. Unlinked: the way in. Linked: who with,
           and the way out — because a link the patient cannot see is one they
           cannot undo, and both sides are allowed to undo it. -->
      <Button
        v-if="auth.hasTherapist"
        variant="linked"
        :linked-label="t('linked', { name: practiceLabel })"
        @click="showUnlinkDialog = true"
      />
      <Button v-else variant="pair" @click="openPair" />
    </template>

    <PairDialog v-model:show="showPairDialog" />
    <UnlinkDialog v-model:show="showUnlinkDialog" :practice-name="practiceLabel" />

    <!-- Frog art. Two renderings of the same mascot, chosen by tier:
         - tablet: the full-viewport frog, cover-fit bottom-right, as designed.
         - phone (every launch route): a tight corner crop, sized off the
           viewport width so it stays a sensible size on any phone and reads as a
           mascot peeking from the corner instead of a huge zoomed slab.
         v-if (not CSS display) so the 1.5 MB full image never downloads on a
         phone that only needs the ~370 KB crop. -->
    <picture
      v-if="!isPhone"
      class="background"
      :class="{ blurred: isFrogBlurred, capped: isWeb }"
      aria-hidden="true"
    >
      <img :src="launchBackground" alt="Frog" />
    </picture>
    <!-- No blur on the phone corner frog — even on launch-terms (which blurs the
         full-viewport frog on tablet via isFrogBlurred) the phone treatment keeps
         the mascot sharp, matching the first-start screen. -->
    <picture v-else class="frog-corner" aria-hidden="true">
      <img :src="frogCorner" alt="Frog" />
    </picture>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import Button from '@/components/Button.vue'
import PairDialog from '@/components/PairDialog.vue'
import PortalButton from '@/components/PortalButton.vue'
import UnlinkDialog from '@/components/UnlinkDialog.vue'
import { useIsPhone } from '@/composables/useViewport'
import { isWeb } from '@/lib/platform'
import { useAuthStore } from '@/stores/auth'

import logoIcon from '@/assets/logo/light/icon.svg?no-inline'
import launchBackground from '@/assets/img/background.png'
import frogCorner from '@/assets/img/frog-corner.png'

// Per-route opt-in allow-lists. Adding a new route here is the single
// source of truth — both the toggle below and any future "which screens
// look like this?" question are answered in this one place.
const TRANSPARENT_ROUTES = new Set<string>(['launch-trial-patient'])
const SHOW_TOP_CHROME_ROUTES = new Set<string>(['launch-trial-patient'])
// Only LaunchTerms used to inject the frog blur via an unscoped <style>
// block. The blur belongs to the design of that screen — see
// docs/screens/launch-terms.png — not to the layout writ large.
// docs/screens/launch-first-start.png and the launch-trial-patient
// mockup both render the frog sharp.
const BLURRED_FROG_ROUTES = new Set<string>(['launch-terms'])

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { isPhone } = useIsPhone()
const auth = useAuthStore()
const showPairDialog = ref(false)
const showUnlinkDialog = ref(false)

/**
 * The practice to name in the chrome and in the unlink dialog.
 *
 * `practices.name` is nullable — Therapist\SessionController creates a practice
 * row straight from a JWT with no name — and in practice it is always set, but
 * the type says otherwise and "verknüpft mit" with nothing after it would be
 * worse than a generic noun. Resolved once here so the label and the dialog
 * title can never name the practice differently.
 */
const practiceLabel = computed(() => auth.practiceName ?? t('unnamed-practice'))
const isTransparent = computed(() => TRANSPARENT_ROUTES.has(String(route.name)))
// Route name as a class hook (e.g. `route-launch-terms`) so the scoped CSS can
// tune a single launch screen — currently just the phone logo size on terms.
const routeClass = computed(() => (route.name ? `route-${String(route.name)}` : ''))
const showTopChrome = computed(() => SHOW_TOP_CHROME_ROUTES.has(String(route.name)))
const isFrogBlurred = computed(() => BLURRED_FROG_ROUTES.has(String(route.name)))

function openSettings() {
  void router.push({ name: 'patient-settings' })
}

function openPair() {
  showPairDialog.value = true
}
</script>

<style scoped lang="css">
section {
  width: 100dvw;
  min-height: 100dvh;

  background: var(--backgroundLaunch);

  display: flex;
  justify-content: center;
  /* Without flex-start here the card stretches to fill the section's
     min-height (flex default is `stretch` for both align-items and
     align-content), which makes the white box tower over its controls.
     The reference designs (docs/screens/launch-first-start.png and
     launch-terms.png) hug their content — that's what we want. */
  align-items: flex-start;
  align-content: flex-start;
  flex-wrap: wrap;

  /* Scoped to .container (not just `div`) because additional sibling divs
     are rendered alongside it for the launch chrome (e.g. div.cog). The
     broad `div` selector would accidentally apply card chrome to those. */
  div.container {
    max-width: 500px;

    display: grid;
    grid-template-columns: 1fr;
    justify-content: center;
    align-content: flex-start;
    align-items: center;
    row-gap: clamp(1rem, 4dvh, 2rem);

    /* Card chrome — used to live as unscoped styles in
       LaunchFirstStart.vue / LaunchTerms.vue, which leaked between routes
       (the card only appeared on screens you'd already navigated through).
       Belongs here so every launch-flow screen renders consistently. */
    background: rgba(243, 246, 252, 1);
    border-radius: 15px;
    padding: 1.5rem;

    /* Bound the card to the viewport and scroll its body internally rather
       than letting the page scroll. On a short landscape tablet (7", ~600px
       tall) the legal-terms card is taller than the screen; without this the
       page scrolls and crops the logo off the top, which looks broken. The
       clamped margin keeps generous breathing room on tall devices
       (phones/iPads) where the content fits and no scrollbar appears, but
       shrinks on short screens so the rounded card stays fully on screen. */
    margin: clamp(1rem, 5dvh, 4rem) auto;
    max-height: calc(100dvh - 2 * clamp(1rem, 5dvh, 4rem));
    overflow-y: auto;
    overscroll-behavior: contain;

    position: relative;
    z-index: 5;

    /* Routes opted into TRANSPARENT_ROUTES drop the card entirely so the
       screen sits directly on --backgroundLaunch (the design language of
       launch-trial-patient). Other launch screens keep the card. */
    &.transparent {
      background: transparent;
      border-radius: 0;
      padding: 0;
    }

    /* The therapist web build draws no card at all: logo, copy and buttons sit
       straight on the launch field beside the frog. Only the paint goes —
       padding, max-height and the internal scroll stay, so the composition is
       pixel-identical to the carded one minus the box. By platform, like the
       frog cap: the patient tablets keep their onboarding cards. */
    &.unboxed {
      background: transparent;
    }

    picture.logo {
      width: 100%;
      height: fit-content;

      display: flex;
      justify-content: center;

      img {
        width: 175px;
        height: auto;
      }
    }
  }

  /* Top-of-viewport chrome (cog left, pair right). max(...) clears the
     iOS notch / Dynamic Island when running under Capacitor; the env()
     fallback to 0px keeps web identical. The cog is wrapped in div.cog
     so Button.settings's `height: 100%` resolves against the wrapper
     (auto height → content height) rather than the viewport (gigantic). */
  div.cog {
    position: fixed;
    top: max(1rem, var(--safe-area-inset-top));
    left: 1rem;
    z-index: 6;
  }

  .button.pair,
  .button.linked {
    position: fixed;
    top: max(1rem, var(--safe-area-inset-top));
    right: 1rem;
    z-index: 6;
  }

  /* The portal button (PortalButton's root) — same corner as the pair button,
     which it never shares a build with. Above the card (z 5) and the frog. */
  button.digola {
    position: fixed;
    top: max(1rem, var(--safe-area-inset-top));
    right: 1rem;
    z-index: 6;
  }

  :deep(nav) {
    background: transparent;

    position: fixed;
    /* Below the system status bar so the back arrow + h1 don't get clipped
       by the system clock / battery / Dynamic Island. Falls back to 0 on
       the web build. */
    top: var(--safe-area-inset-top);
    left: 0;
  }

  :deep(div.status) {
    width: 100dvw;

    display: flex;
    column-gap: 0.5rem;

    position: fixed;
    bottom: 1rem;
    left: 1rem;

    img {
      width: 1rem;
      height: auto;

      display: block;
    }
  }

  :deep(h1) {
    text-align: center;

    /* Headings render in the i18n string's own casing — e.g. "Willkommen bei
       LingoTalk!" and "LingoTalk für …" keep the LingoTalk wordmark (capital
       L + T) and a leading capital. No text-transform here. */
  }

  :deep(div.actions) {
    width: 100%;

    display: grid;
    justify-content: center;
    row-gap: 1.25rem;

    position: relative;
    z-index: 4;

    margin-top: 1rem;

    button {
      width: fit-content;
      min-width: 250px;
    }
  }

  :deep(div.trial) {
    width: 100dvw;

    font-size: 1.25rem;
    font-weight: 600;
    text-align: center;

    background: rgba(68, 172, 105, 1);
    color: var(--white);

    padding: 1.15ch 1rem;

    position: fixed;
    z-index: 2;
    left: 0;
    right: 0;
    bottom: 7.5dvh;
  }

  picture.background {
    width: 100dvw;
    height: 100dvh;

    position: absolute;
    z-index: 1;

    overflow: hidden;

    /* The launch background image carries its own blue field around the
       frog, so we scale it to cover the whole viewport with the frog
       anchored bottom-right via object-position. cover (not contain) so
       there's no seam between the image's baked-in background and the
       section's CSS background-color on mismatched aspect ratios.
       Routes opted into BLURRED_FROG_ROUTES — currently launch-terms —
       apply blur(10px) brightness(80%) so the legal-text screen reads
       cleanly without competing with the frog. */
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: bottom right;
    }

    &.blurred img {
      filter: blur(10px) brightness(80%);
    }

    /* The therapist build runs in a desktop browser, where "the viewport" can
       be a 27" monitor: cover-fitting 100dvw × 100dvh scales the mascot with
       the screen, and on a large one it dwarfs the card. Cap the box it covers
       at the design canvas (the nav's 1440px wide, the 12.9" iPad's 1024px
       tall) and pin it to the corner: inside the cap this is exactly the rule
       above; beyond it the frog stops growing. No seam, because the art's own
       field is exactly --backgroundLaunch (rgb(204, 232, 245) along every
       edge). Web only, by platform: the patient tablets render the
       full-viewport frog as designed, and an Android tablet in portrait would
       otherwise lose a fifth of its mascot to the height cap. Fixed rather
       than absolute so it sits in the viewport corner, not wherever the
       section's static position puts it.
       The 0.85 factor shrinks the mascot a further 15% on the therapist
       screen: the design canvas alone still left it a touch large beside
       the card. Both axes scale together so cover-fit keeps the frog's
       proportions and the field colour still hides the edge. */
    &.capped {
      width: calc(min(100dvw, 1440px) * 0.85);
      height: calc(min(100dvh, 1024px) * 0.85);

      position: fixed;
      right: 0;
      bottom: 0;
    }
  }

  /* The CTA teleport target only exists as a positioned band on phones; on
     tablet the button stays inside the card, so keep the empty div out of the
     flex flow entirely. */
  #launch-action {
    display: none;
  }

  /* Trial-home vertical centring — the one TRANSPARENT_ROUTE
     (launch-trial-patient), on every width. The carded onboarding screens stay
     top-anchored on purpose (see align-items: flex-start above): a card should
     hug its content rather than tower over it. This screen is the opposite — a
     full-field composition where the logo, heading and actions sit centred in
     the blue with the frog beside them, as in
     docs/screens/launch-trial-patient-expired.png.
     
     It regressed in 1fcad79, which replaced the container's `margin: 10dvh
     auto` with `margin: clamp(1rem, 5dvh, 4rem) auto` to keep the tall
     legal-terms card on a short 16:10 tablet. That was right for the cards and
     wrong here: the 4rem ceiling means the taller the tablet, the smaller the
     share of the viewport above the content, so a 12.9" iPad got 64px where it
     used to get ~137px and the screen read as jammed against the top. The phone
     tier already fixed this for itself below; this is the same fix, unscoped.
     
     `flex-wrap: nowrap` is what makes it work at all: with the section's
     default wrap + align-content: flex-start, a lone flex line is packed to the
     top and sized to its content, so there is no vertical slack for `margin:
     auto` to distribute and it resolves to 0. nowrap makes the single line fill
     the section's cross size instead. */
  &:not(.carded) {
    flex-wrap: nowrap;

    padding-top: var(--safe-area-inset-top);

    /* Reserve the trial banner's band (fixed at bottom: 7.5dvh, ~3.4rem tall)
       so centred content can never sit under it. Reserved UNCONDITIONALLY,
       including for a paid entitlement that renders no banner: the four banner
       states resolve asynchronously, and letting the band appear and disappear
       would slide the whole composition up and down as the entitlement check
       settles. */
    padding-bottom: calc(7.5dvh + 3.5rem);
  }

  &:not(.carded) div.container.transparent {
    /* The auto cross-axis margin the nowrap line above gives slack to; it
       overrides the base rule's clamped top margin. max-height + overflow-y
       from that rule still apply, so a short screen keeps its scroll fallback
       instead of cropping. */
    margin: auto;
  }

  /* Phone tier (portrait phones / very narrow web). 600px is the phone↔tablet
     boundary used across the patient app (matches app.css's --screen-pad-x
     override and the native sw600dp split), so tablets never see these rules
     and their tuned, reference-matched framing is unchanged.

     On tablet the carded launch screens read as a floating rounded modal
     centered over the blue field with the frog beside it
     (docs/screens/launch-first-start.png). On a narrow phone the fluid tablet
     tuning collapses that into a full-bleed panel hugging the top, hiding the
     frog behind it. These overrides restore the floating-modal feel in
     portrait: center the card, give it side gutters + more breathing room, and
     leave a real top/bottom gap so the mascot peeks out.

     Scoped to :not(.transparent) so the intentionally top-anchored trial-home
     screen (launch-trial-patient, the only TRANSPARENT_ROUTES member — see
     docs/screens/launch-trial-patient-expired.png) keeps its clamp top margin
     and flex-start anchoring untouched. */
  @media (max-width: 600px) {
    /* Everything here targets the carded onboarding routes (launch-first-start,
       launch-terms). The transparent trial-home keeps its full-frog background
       and top-anchored layout — it is excluded via section.carded and the
       :not(.transparent) container guard, and its frog is still the full
       picture.background (v-if in the template). */
    /* Corner-frog geometry, shared across every phone launch route (the carded
       card-clearance math, the corner frog on both onboarding + trial-home, and
       the CTA band) so they can never drift apart. --frog-w is viewport-width
       driven (not text-driven) so the mascot is a consistent size on any phone;
       --frog-h derives from the crop's aspect ratio (720×592 ⇒ 1.216). */
    --launch-gutter: 1.25rem;
    --frog-w: clamp(11rem, 55vw, 19rem);
    --frog-h: calc(var(--frog-w) / 1.216);

    &.carded {
      /* Force a single, full-height flex line so margin: auto can vertically
         center the card. With the section's default flex-wrap: wrap +
         align-content: flex-start a lone line is packed to the top and sized to
         its content, leaving no vertical slack — auto margins then resolve to 0
         and the card jams under the notch. nowrap makes the single line fill the
         section's cross size instead. */
      flex-wrap: nowrap;

      /* Reserve the bottom band for the corner frog + relocated CTA, and clear
         the notch at the top. margin: auto then centers the card in what's left,
         so it can never overlap the frog no matter the card's height or the
         phone's size / dynamic-type scale. */
      padding-top: var(--safe-area-inset-top);
      padding-bottom: var(--frog-h);
    }

    div.container:not(.transparent) {
      /* Vertical + horizontal centering within the padded region above the frog
         band — the full-height single line (nowrap, above) gives the auto
         cross-axis margin its slack, overriding the base rule's clamp margin. */
      margin: auto;

      /* Side gutters so the rounded card floats over the blue field. */
      max-width: min(500px, calc(100dvw - 2 * var(--launch-gutter)));

      /* More spacing — up from the shared 1.5rem — inside the card. */
      padding: 2rem 1.75rem;
      row-gap: clamp(1.25rem, 4dvh, 2rem);

      /* Bounded to the space above the frog band (and below the notch); a taller
         card — e.g. the terms card — scrolls internally past this rather than
         growing into the frog. */
      max-height: calc(100dvh - var(--frog-h) - var(--safe-area-inset-top) - 1.5rem);
    }

    /* The 175px logo is tuned for the roomy first-start card; on the denser
       terms card and the trial-home screen it dominates the phone viewport, so
       shrink it there. Phone-only, route-scoped; first-start and every tablet
       render keep the full-size logo. */
    &.route-launch-terms div.container picture.logo img,
    &.route-launch-trial-patient div.container picture.logo img {
      width: 120px;
    }

    /* Trial-home: center the card-less content (logo + heading + actions) rather
       than top-anchoring it, and give the background mascot more room. nowrap
       makes the single flex line fill the height so margin: auto centers; the
       paddings keep the content clear of the notch/chrome above and the trial
       banner below. The larger --frog-w lets the frog grow a bit — it reads as
       background here, so it can be bolder than on the onboarding cards. */
    &:not(.carded) {
      flex-wrap: nowrap;
      padding-top: var(--safe-area-inset-top);
      padding-bottom: calc(5rem + var(--safe-area-inset-bottom));

      --frog-w: clamp(16rem, 82vw, 30rem);
    }

    &:not(.carded) div.container.transparent {
      margin: auto;
    }

    /* Trial banner — flush to the bottom edge on phones (tablet floats it at
       7.5dvh) and padded for the home indicator, across the green / red / paid
       states. The content layout (stacking countdown + Preise) lives in
       LaunchTrialPatient.vue's own phone block. */
    :deep(div.trial) {
      bottom: 0;
      padding: 0.85rem 1.25rem calc(0.85rem + var(--safe-area-inset-bottom));
      font-size: 1.05rem;
    }

    /* The mascot, cropped tight and docked bottom-LEFT, mirrored (scaleX(-1)) so
       it faces inward. Sized off --frog-w (height auto keeps the crop's aspect
       ratio); the crop's own field is exactly --backgroundLaunch, so the corner
       blends seamlessly with no visible edge. z-index 1 keeps it behind the card
       and, on trial-home, behind the trial banner (which peeks over it). On
       carded screens the relocated CTA sits to its right; on trial-home it's
       purely background — where a larger --frog-w (below) lets it grow. */
    picture.frog-corner {
      width: var(--frog-w);
      height: auto;

      position: fixed;
      left: 0;
      bottom: 0;
      z-index: 1;

      img {
        width: 100%;
        height: auto;

        display: block;

        transform: scaleX(-1);
      }
    }

    /* CTA band — bottom-RIGHT, right edge aligned to the card's gutter, filling
       the space to the right of the frog and vertically centered against it. The
       teleported button lands here on phones (see LaunchFirstStart). Hidden and
       out of flow otherwise (base rule below the media query). Carded-only so
       the empty band never activates on the transparent trial-home. */
    &.carded #launch-action {
      height: var(--frog-h);

      display: flex;
      align-items: center;
      justify-content: flex-end;

      position: fixed;
      left: var(--frog-w);
      right: var(--launch-gutter);
      bottom: 0;
      z-index: 4;

      /* Drop the next-variant's own `margin: 0 auto` (which would center it in
         the band) so it stays hard-right against the card edge. */
      :deep(.button.next) {
        margin: 0;
      }
    }
  }
}
</style>

<i18n>
{
  "de": {
    "linked": "verknüpft mit {name}",
    "unnamed-practice": "einer Therapeut:in"
  },
  "en": {
    "linked": "linked with {name}",
    "unnamed-practice": "a therapist"
  }
}
</i18n>
